import { useSyncExternalStore } from 'react';

/**
 * Light or dark (DESIGN-SYSTEM §12).
 *
 * The person's choice — Light, Dark or Match system — is kept in this browser (it is a preference of the
 * device, not of the company), and light is the default for anybody who never chose. `theme-init.js`, a
 * same-origin script in each app's `public/`, applies the same rule before the first paint so a dark choice
 * never flashes white; this module keeps it right afterwards.
 *
 * One store for the whole page, so every switch on screen and the page itself always agree; it follows the
 * system's appearance when asked to, and another tab's change.
 */

export type ThemeChoice = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

/** The one storage key, shared with `theme-init.js`. */
export const THEME_STORAGE_KEY = 'timely.theme';

const isChoice = (v: unknown): v is ThemeChoice => v === 'light' || v === 'dark' || v === 'system';

export function readThemeChoice(): ThemeChoice {
    try {
        const v = window.localStorage.getItem(THEME_STORAGE_KEY);
        return isChoice(v) ? v : 'light';
    } catch {
        return 'light';
    }
}

const systemPrefersDark = (): boolean => {
    try { return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches; } catch { return false; }
};

export const resolveTheme = (choice: ThemeChoice): Theme =>
    choice === 'dark' || (choice === 'system' && systemPrefersDark()) ? 'dark' : 'light';

/**
 * Draws the page in the chosen theme: `data-theme` on <html>, and the browser's own chrome to match.
 *
 * A change of theme happens at once, everywhere. Without care every control with a colour transition would fade
 * on its own clock and the page would ripple from one theme to the other; for the frame of the change
 * `data-theme-switching` holds every transition still (theme.css), except the switch's own sliding mark.
 */
export function applyTheme(choice: ThemeChoice): Theme {
    const theme = resolveTheme(choice);
    const root = document.documentElement;
    const changing = root.getAttribute('data-theme') !== null && root.getAttribute('data-theme') !== theme;
    if (changing) root.setAttribute('data-theme-switching', '');
    root.setAttribute('data-theme', theme);
    if (changing) {
        // Read a style so the new colours are applied while transitions are held, then let them go.
        void window.getComputedStyle(root).color;
        const release = () => root.removeAttribute('data-theme-switching');
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(release)); else release();
    }
    root.setAttribute('data-theme-choice', choice);
    const meta = document.head.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (meta) meta.content = theme === 'dark' ? '#050B16' : '#0A1A33';
    return theme;
}

/* ------------------------------- the store -------------------------------- */

let snapshot: string | null = null;            // "choice|theme"
/** The choice made on this page, which also holds where the browser keeps nothing (a private window). */
let chosen: ThemeChoice | null = null;
const currentChoice = (): ThemeChoice => chosen ?? readThemeChoice();
const listeners = new Set<() => void>();
const notify = () => { snapshot = null; listeners.forEach(l => l()); };

function getSnapshot(): string {
    if (snapshot === null) {
        const choice = currentChoice();
        snapshot = `${choice}|${resolveTheme(choice)}`;
    }
    return snapshot;
}

function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    if (listeners.size === 1) attach();
    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) detach();
    };
}

let detachers: (() => void)[] = [];
function attach() {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
        if (e.key !== THEME_STORAGE_KEY) return;
        chosen = readThemeChoice();
        applyTheme(chosen);
        notify();
    };
    window.addEventListener('storage', onStorage);
    detachers.push(() => window.removeEventListener('storage', onStorage));
    if (typeof window.matchMedia === 'function') {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const onSystem = () => {
            if (currentChoice() !== 'system') return;
            applyTheme('system');
            notify();
        };
        mq.addEventListener?.('change', onSystem);
        detachers.push(() => mq.removeEventListener?.('change', onSystem));
    }
}
function detach() { detachers.forEach(d => d()); detachers = []; }

/** Keeps and applies a choice, and tells every switch on the page. */
export function setThemeChoice(choice: ThemeChoice): void {
    chosen = choice;
    try { window.localStorage.setItem(THEME_STORAGE_KEY, choice); } catch { /* a private window: this page only */ }
    applyTheme(choice);
    notify();
}

/** The theme for a React tree: the choice, what it resolves to now, and the setter. */
export function useTheme(): { choice: ThemeChoice; theme: Theme; setChoice: (c: ThemeChoice) => void } {
    const snap = useSyncExternalStore(subscribe, getSnapshot, () => 'light|light');
    const [choice, theme] = snap.split('|') as [ThemeChoice, Theme];
    return { choice, theme, setChoice: setThemeChoice };
}
